/** ModernFusion — RSVP. Minimalist with dietary preferences. */

import React from 'react';
import { motion } from 'framer-motion';
import { Mail, CalendarClock, Utensils } from 'lucide-react';
import { MODERN_EASE } from '../theme';
import type { ModernContent } from '../content';
import { useRsvp } from '@/src/modules/rsvp';

export function RsvpSection({
  content,
  invitationId,
  isLive = false,
}: {
  content: ModernContent['rsvp'];
  invitationId: string | null;
  isLive?: boolean;
}) {
  const [name, setName] = React.useState('');
  const [attending, setAttending] = React.useState<'yes' | 'no' | ''>('');
  const [dietary, setDietary] = React.useState('');

  const { state, errorMessage, submit } = useRsvp(invitationId, isLive);
  const sent = state === 'success';
  const sending = state === 'loading';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !attending || sending) return;
    await submit({
      guestName: name.trim(),
      status: attending === 'yes' ? 'ACCEPTED' : 'DECLINED',
      headcount: 1,
      message: dietary.trim() ? `Dietary: ${dietary.trim()}`.slice(0, 500) : undefined,
    });
  };

  return (
    <section
      id="rsvp"
      className="px-6 py-20 text-center"
      style={{ background: '#faf9f6', color: '#2c2c2c' }}
    >
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: MODERN_EASE }}
        className="text-sm uppercase tracking-[0.3em] text-[#87a878]"
      >
        {content.label}
      </motion.p>
      <h2 className="mt-2 font-serif text-4xl font-semibold text-[#5a7a52]">{content.title}</h2>
      <p className="mx-auto mt-4 max-w-xl text-[#6b6b6b]">{content.description}</p>

      <div className="mt-6 inline-flex items-center gap-2 text-sm text-[#87a878]">
        <CalendarClock className="h-4 w-4" />
        Kindly respond by {content.deadline}
      </div>

      {content.externalLink ? (
        <div className="mt-8">
          <a
            href={content.externalLink}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#87a878] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white shadow-lg transition-transform hover:scale-105"
          >
            <Mail className="h-4 w-4" /> RSVP Here
          </a>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mx-auto mt-8 max-w-md space-y-4">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full rounded-lg border border-[rgba(135,168,120,0.2)] bg-white px-4 py-3 text-center outline-none focus:border-[#87a878]"
          />
          <div className="flex justify-center gap-3">
            {(['yes', 'no'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setAttending(opt)}
                className={`rounded-full px-6 py-2 text-sm font-semibold capitalize transition ${
                  attending === opt
                    ? 'bg-[#87a878] text-white'
                    : 'border border-[rgba(135,168,120,0.3)] text-[#6b6b6b]'
                }`}
              >
                {opt === 'yes' ? 'Joyfully accepts' : 'Regretfully declines'}
              </button>
            ))}
          </div>
          {content.dietaryNote && (
            <div className="flex items-center gap-2 rounded-lg border border-[rgba(135,168,120,0.2)] bg-white px-4 py-3">
              <Utensils className="h-4 w-4 text-[#87a878]" />
              <input
                value={dietary}
                onChange={(e) => setDietary(e.target.value)}
                placeholder={content.dietaryNote}
                className="flex-1 bg-transparent outline-none"
              />
            </div>
          )}
          {state === 'error' && errorMessage && (
            <p className="text-sm font-medium text-red-600">{errorMessage}</p>
          )}
          <button
            type="submit"
            disabled={!name || !attending || sending || sent}
            className="w-full rounded-full bg-[#c9a9a6] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white shadow-lg transition-transform hover:scale-[1.02] disabled:opacity-40"
          >
            {sent ? 'Thank you!' : sending ? 'Sending…' : 'Send RSVP'}
          </button>
        </form>
      )}
    </section>
  );
}
